import React, { useState } from "react";
import { FormulaBuilderModal } from "./FormulaBuilderModal";
import { Button } from "./button";
import { ArrowRight } from "lucide-react";

const formulaExamples = [
  {
    title: "Prosty warunek IF – Pokaż tekst tylko dla mężczyzn",
    description: "Formuła wyświetli konkretny tekst tylko wtedy, gdy w polu 'płeć' zostanie wpisana wartość 'M'. Jeśli pole będzie puste lub będzie miało inną wartość, tekst się nie pojawi. Zobacz, co się stanie dla różnych wartości.",
    formula: "<<#if wiek (>18) == 'Tak'>>Ta osoba jest pełnoletnia<</if>>",
    scenarios: [
      {
        input: { variable: 'wiek (>18)', value: 'Tak' },
        result: "Ta osoba jest pełnoletnia"
      },
      {
        input: { variable: 'wiek (>18)', value: '' },
        result: ""
      }
    ]
  },
  {
    title: "Warunek IF/ELSE – Inny tekst dla klienta biznesowego i indywidualnego",
    description: "Jeśli 'typ_klienta' to 'Biznes', pojawi się jeden tekst. W każdym innym przypadku (dzięki 'else') zostanie wyświetlony tekst dla klientów indywidualnych.",
    formula: "Szanowni Państwo,\n<<#if typ_klienta == 'Biznes'>>\ndziękujemy za zaufanie, jakim obdarzyli Państwo naszą firmę.\n<<#else>>\ncieszymy się, że dołączyli Państwo do grona naszych klientów indywidualnych.\n<</if>>",
    scenarios: [
      {
        input: { variable: 'typ_klienta', value: 'Biznes' },
        result: "Szanowni Państwo,\ndziękujemy za zaufanie, jakim obdarzyli Państwo naszą firmę."
      },
      {
        input: { variable: 'typ_klienta', value: 'Prywatny' },
        result: "Szanowni Państwo,\ncieszymy się, że dołączyli Państwo do grona naszych klientów indywidualnych."
      }
    ]
  },
  {
    title: "Warunek IF/ELSEIF/ELSE – Wybór opiekuna na podstawie regionu",
    description: "Formuła przypisuje opiekuna na podstawie regionu. Zobacz, jak działają wszystkie trzy gałęzie warunku: dla 'północ', 'południe' i każdej innej wartości.",
    formula: "Twoim dedykowanym opiekunem jest:\n<<#if region == 'północ'>>Anna Nowak<<#elseif region == 'południe'>>Piotr Malinowski<<#else>>Dział Obsługi Klienta (kontakt@firma.pl)<</if>>",
    scenarios: [
      {
        input: { variable: 'region', value: 'północ' },
        result: "Twoim dedykowanym opiekunem jest:\nAnna Nowak"
      },
      {
        input: { variable: 'region', value: 'południe' },
        result: "Twoim dedykowanym opiekunem jest:\nPiotr Malinowski"
      },
      {
        input: { variable: 'region', value: 'wschód' },
        result: "Twoim dedykowanym opiekunem jest:\nDział Obsługi Klienta (kontakt@firma.pl)"
      }
    ]
  }
];

export default function FormulaGuidePage() {
  const [isFormulaModalOpen, setIsFormulaModalOpen] = useState(false);
  const formulaVariables = ["status", "warunek1", "warunek2", "plec", "nip", "typ_klienta", "region"];

  return (
    <div style={{ width: '100vw', maxWidth: 'none', margin: 0, padding: 0, overflowX: 'hidden' }}>
      <div style={{ width: '100vw', maxWidth: 'none', margin: 0, padding: 48 }}>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: "#2563eb", marginBottom: 0 }}>Jak przygotować szablon Word do automatyzacji?</h1>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#2563eb', margin: '50px 0 0px 0' }}>• Jak przygotować dokument do automatyzacji w podstawowej formie?</h2>
        <div style={{ background: '#f6f8fa', borderRadius: 18, padding: 36, marginBottom: 40, fontSize: 19, color: '#1a1a1a', fontWeight: 500, lineHeight: 1.8, boxShadow: '0 4px 24px #e0e7ff55' }}>
          <ul style={{ paddingLeft: 32, margin: 0 }}>
            <li>Szablon musi być w formacie <b>.docx</b>.</li>
            <li>Unikaj zabezpieczeń w pliku.</li>
            <li>Oznacz miejsca na dane do automatyzacji np. <code>{'<<Imie i Nazwisko>>'}</code></li>
            <li>Możesz dodać inne pola np. <code>{'<<Email>>'}</code>.</li>
            <li>Pola w znaczkach <code>&lt;&lt; &gt;&gt;</code> będą pokazane w formularzu automatyzacji i wypełniane przez system – zarówno w formularzu, jak i w pliku Excel użytkownik będzie mógł wpisać wartość dla każdej zmiennej. To, co wpisze, pojawi się w wygenerowanym dokumencie.</li>
            <li>Między <code>&lt;&lt;</code> a nazwą zmiennej <b>nie powinno być spacji</b> – poprawnie: <code>{'<<Imie i Nazwisko>>'}</code>, błędnie: <code>{'<< Imie i Nazwisko >>'}</code>.</li>
            <li>Możesz formatować zmienne w Wordzie – np. pogrubić, pochylić, podkreślić. Jeśli pogrubisz <b><code>{'<<Imie i Nazwisko>>'}</code></b> w szablonie, to po wygenerowaniu dokumentu wstawiona wartość również będzie pogrubiona.</li>
          </ul>
          <div style={{marginTop: 14, color: '#444', fontSize: 17}}>
            <b>Przykład formatowania dla dokumentu przygotowanego w Wordzie:</b><br/>
            <ul style={{margin: '8px 0 0 18px', padding: 0}}>
              <li>Pogrubienie: <b><code>{'<<Imie i Nazwisko>>'}</code></b> – wstawiona wartość też będzie pogrubiona.</li>
              <li>Kursywa:  <i><code>{'<<Email>>'}</code></i> – wstawiona wartość będzie kursywą.</li>
              <li>Podkreślenie: <u><code>{'<<NIP>>'}</code></u> – wstawiona wartość będzie podkreślona.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#2563eb', margin: '0 0 24px 0' }}>• Jak przygotować dokument do automatyzacji w rozbudowanej formie z warunkami?        </h2>
        <h2 style={{ fontSize: 32, fontWeight: 800, margin: "56px 0 16px 0", textAlign: 'center', color: '#2563eb' }}>Praktyczne przykłady użycia warunków</h2>
        <p style={{textAlign: 'center', fontSize: 18, color: '#42563eb44', marginBottom: 32}}>Zobacz, jak działają formuły warunkowe na konkretnych przykładach i spróbuj sam!</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
          {formulaExamples.map((ex, idx) => (
            <div key={idx} style={{ background: "#fff", border: '2px solid #e0e7ff', borderRadius: 20, padding: '28px 32px', boxShadow: "0 4px 32px #e0e7ff66" }}>
              <h3 style={{ fontWeight: 700, fontSize: 22, color: "#2563eb", marginBottom: 10 }}>{idx + 1}. {ex.title}</h3>
              <p style={{ color: "#444", fontSize: 17, marginBottom: 24, lineHeight: 1.7 }}>{ex.description}</p>
              
              <div style={{ marginBottom: 20 }}>
                <div style={{fontWeight: 600, fontSize: 16, color: '#333', marginBottom: 8}}>1. Formuła do wklejenia w Wordzie:</div>
                <pre style={{ background: "#f6f8fa", border: '1px solid #e0e7ff', borderRadius: 12, padding: 18, fontSize: 16, fontFamily: "monospace", color: "#222", whiteSpace: "pre-wrap" }}>{ex.formula}</pre>
              </div>

              <div>
                <div style={{fontWeight: 600, fontSize: 16, color: '#333', marginBottom: 16}}>2. Zobacz, jak to działa dla różnych danych:</div>
                <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
                  {ex.scenarios.map((scenario, s_idx) => (
                    <div key={s_idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 24}}>
                      <div>
                        <div style={{ background: "#f6f8fa", border: '1px solid #e0e7ff', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 74 }}>
                          <span style={{color: '#64748b', fontSize: 14, fontWeight: 500}}>Wprowadzona dana dla: <b>{scenario.input.variable}</b></span>
                          <span style={{color: '#1a1a1a', fontSize: 17, fontWeight: 600}}>{scenario.input.value}</span>
                        </div>
                      </div>

                      <ArrowRight size={32} color="#2563eb" />

                      <div>
                        <div style={{ background: "#e0e7ff", border: '1px solid #2563eb', borderRadius: 12, padding: '16px 20px', minHeight: 74, display: 'flex', alignItems: 'center' }}>
                          <span style={{color: '#2563eb', fontSize: 17, fontWeight: 600, whiteSpace: 'pre-wrap', fontStyle: scenario.result ? 'normal' : 'italic', opacity: scenario.result ? 1 : 0.7}}>{scenario.result || '(brak tekstu)'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#2563eb', margin: '34px 0 34px 0' }}>• Kreator Formuł Logicznych – przygotuj rozszerzone warunki automatycznie z uyciem kreatora</h2>
        <div style={{ background: '#e0e7ff', borderRadius: 18, padding: 32, marginBottom: 48, fontSize: 18, color: '#222', fontWeight: 500, lineHeight: 1.8, boxShadow: '0 4px 24px #c7d2fe55' }}>
          <p><b>Kreator Formuł Logicznych</b> to narzędzie, które pozwala w prosty sposób dodać do szablonu Word warunki typu <b>JEŚLI... TO...</b> (tzw. IF/ELSE). Dzięki temu możesz np. wyświetlić w dokumencie inny tekst w zależności od płci, statusu, czy innej zmiennej.</p>
          <ul style={{ margin: '18px 0 0 24px', padding: 0 }}>
            <li><b>Nie musisz znać składni!</b> Wystarczy, że wybierzesz lub wpiszesz nazwę zmiennej, wybierzesz warunek i wpiszesz tekst, który ma się pojawić.</li>
            <li><b>Podpowiedzi i przykłady</b> – kreator podpowiada zmienne i pokazuje gotowy kod do skopiowania.</li>
            <li><b>Wklej gotową formułę</b> w odpowiednie miejsce w szablonie Word – system sam ją rozpozna i uzupełni podczas generowania dokumentu.</li>
          </ul>
          <p style={{ marginTop: 18, color: '#2563eb', fontWeight: 600, fontSize: 17 }}>To narzędzie jest stworzone z myślą o pracownikach biurowych – nie musisz być informatykiem, żeby korzystać z automatyzacji! Kreator znajdziesz poniżej.</p>
        </div>

        <div style={{textAlign: 'center', marginTop: 56}}>
          <h2 style={{ fontSize: 28, fontWeight: 700, margin: "38px 0 16px 0" }}>Stwórz własną formułę</h2>
          <p style={{ fontSize: 18, color: "#222", marginBottom: 24 }}>
            Czujesz się pewniej? Skorzystaj z graficznego kreatora, aby wygenerować własną, zaawansowaną formułę.
          </p>
          <Button className="upload-button" style={{ padding: '16px 38px', fontSize: 20, marginBottom: 40 }} onClick={() => setIsFormulaModalOpen(true)}>
            Otwórz Kreator Formuł Logicznych
          </Button>
        </div>

        <FormulaBuilderModal open={isFormulaModalOpen} onOpenChange={setIsFormulaModalOpen} variables={formulaVariables} />
      </div>
    </div>
  );
} 